import React from 'react';
import { format, parseISO } from 'date-fns';
import {
  Ship, Plane, GraduationCap, Building2, Home, Palmtree,
  TrendingUp, TrendingDown, Briefcase, FileCheck,
} from 'lucide-react';

export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return format(parseISO(dateStr), 'dd MMM yy');
  } catch (err) {
    return dateStr;
  }
};

export const TRIP_TYPE_COLORS = {
  work_trip: 'border-l-blue-500',
  hotel_stay: 'border-l-purple-500',
  qualifying_holiday: 'border-l-amber-500',
  course_foreign: 'border-l-cyan-500',
  course_uk: 'border-l-slate-500',
  paid_leave: 'border-l-amber-500',
  base_work_uk: 'border-l-slate-500',
  base_work_foreign: 'border-l-indigo-500',
  uk_hotel_stay: 'border-l-slate-500',
  wage_adjustment: 'border-l-orange-500',
  visa_run: 'border-l-emerald-500'
};

export const getTripFlags = (trip) => {
  const isWorkTrip = trip.trip_type === 'work_trip';
  const isHotelStay = trip.trip_type === 'hotel_stay';
  const isUkHotelStay = trip.trip_type === 'uk_hotel_stay';
  const isHoliday = trip.trip_type === 'qualifying_holiday';
  const isCourseUk = trip.trip_type === 'course_uk';
  const isCourseForeign = trip.trip_type === 'course_foreign';
  const isCourse = isCourseUk || isCourseForeign;
  const isPaidLeave = trip.trip_type === 'paid_leave';
  const isWageAdjustment = trip.trip_type === 'wage_adjustment';
  const isBaseWorkUk = trip.trip_type === 'base_work_uk';
  const isBaseWorkForeign = trip.trip_type === 'base_work_foreign';
  const isBaseWork = isBaseWorkUk || isBaseWorkForeign;
  const isVisaRun = trip.trip_type === 'visa_run';
  const isAddition = trip.adjustment_type === 'addition';
  const isDeduction = trip.adjustment_type === 'deduction';
  const isContinuous = trip.no_uk_departure || trip.no_uk_return;
  const isMultiLeg = trip.has_multiple_uk_crossings && trip.uk_water_crossings?.length > 0;
  const isUkBased = isUkHotelStay || isCourseUk || isPaidLeave || isWageAdjustment || isBaseWorkUk;
  const hasPreTravel = trip.pre_travel && (trip.pre_departure_date || (trip.pre_uk_stops && trip.pre_uk_stops.length > 0) || (trip.pre_abroad_stops && trip.pre_abroad_stops.length > 0));
  const hasPostTravel = trip.post_travel && (trip.post_return_date || (trip.post_abroad_stops && trip.post_abroad_stops.length > 0) || (trip.post_uk_stops && trip.post_uk_stops.length > 0));

  return {
    isWorkTrip, isHotelStay, isUkHotelStay, isHoliday, isCourseUk, isCourseForeign, isCourse,
    isPaidLeave, isWageAdjustment, isBaseWorkUk, isBaseWorkForeign, isBaseWork, isVisaRun,
    isAddition, isDeduction, isContinuous, isMultiLeg, isUkBased, hasPreTravel, hasPostTravel,
  };
};

export const getTripTitle = (trip, f) => {
  if (f.isHotelStay) return trip.hotel_name || `Hotel in ${trip.hotel_city || 'Abroad'}`;
  if (f.isUkHotelStay) return trip.hotel_name || `UK Hotel in ${trip.hotel_city || 'UK'}`;
  if (f.isCourse) return trip.course_name || (f.isCourseUk ? 'UK Course' : 'Foreign Course');
  if (f.isPaidLeave) {
    return trip.leave_type === 'annual_leave' ? 'Paid Annual Leave' :
           trip.leave_type === 'sick_leave' ? 'Paid Sick Leave' : 'Paid Leave';
  }
  if (f.isWageAdjustment) return trip.adjustment_description || (f.isDeduction ? 'Wage Deduction' : 'Wage Addition');
  if (f.isBaseWork) return trip.base_name || `Base Work in ${trip.base_location || (f.isBaseWorkUk ? 'UK' : 'Abroad')}`;
  if (f.isVisaRun) return `Visa Run${trip.foreign_ports?.length ? ' — ' + trip.foreign_ports.join(', ') : ''}`;
  if (f.isWorkTrip) return trip.vessel_name || 'At Sea/On Vessel';
  return 'Qualifying Holiday';
};

export const getBadgeStyle = (f) => {
  if (f.isHotelStay) return 'border-amber-500 text-amber-400';
  if (f.isUkHotelStay) return 'border-slate-500 text-slate-400';
  if (f.isCourseUk) return 'border-slate-500 text-slate-400';
  if (f.isCourseForeign) return 'border-teal-500 text-teal-400';
  if (f.isPaidLeave) return 'border-purple-500 text-purple-400';
  if (f.isWageAdjustment && f.isDeduction) return 'border-red-500 text-red-400';
  if (f.isWageAdjustment && f.isAddition) return 'border-amber-500 text-amber-400';
  if (f.isBaseWorkUk) return 'border-slate-500 text-slate-400';
  if (f.isBaseWorkForeign) return 'border-cyan-500 text-cyan-400';
  if (f.isVisaRun) return 'border-emerald-500 text-emerald-400';
  if (f.isWorkTrip) return 'border-blue-500 text-blue-400';
  return 'border-purple-500 text-purple-400';
};

export const getBadgeLabel = (f) => {
  if (f.isHotelStay) return 'Hotel (Abroad)';
  if (f.isUkHotelStay) return 'UK Hotel';
  if (f.isCourseUk) return 'UK Course';
  if (f.isCourseForeign) return 'Course (Abroad)';
  if (f.isPaidLeave) return 'Leave';
  if (f.isWageAdjustment && f.isDeduction) return 'Deduction';
  if (f.isWageAdjustment && f.isAddition) return 'Addition';
  if (f.isBaseWorkUk) return 'UK Base';
  if (f.isBaseWorkForeign) return 'Base (Abroad)';
  if (f.isVisaRun) return 'Visa Run';
  if (f.isWorkTrip) return 'Vessel';
  return 'Holiday';
};

export const getIconBg = (f) => {
  if (f.isHotelStay) return 'bg-amber-500/20';
  if (f.isUkHotelStay) return 'bg-slate-500/20';
  if (f.isCourseUk) return 'bg-slate-500/20';
  if (f.isCourseForeign) return 'bg-teal-500/20';
  if (f.isPaidLeave) return 'bg-purple-500/20';
  if (f.isWageAdjustment && f.isDeduction) return 'bg-red-500/20';
  if (f.isWageAdjustment && f.isAddition) return 'bg-amber-500/20';
  if (f.isBaseWorkUk) return 'bg-slate-500/20';
  if (f.isBaseWorkForeign) return 'bg-cyan-500/20';
  if (f.isVisaRun) return 'bg-emerald-500/20';
  if (f.isWorkTrip) return 'bg-blue-500/20';
  return 'bg-purple-500/20';
};

export const getTripIcon = (f) => {
  if (f.isHotelStay) return <Building2 className="h-5 w-5 text-amber-400" />;
  if (f.isUkHotelStay) return <Home className="h-5 w-5 text-slate-400" />;
  if (f.isCourse) return <GraduationCap className={`h-5 w-5 ${f.isCourseUk ? 'text-slate-400' : 'text-teal-400'}`} />;
  if (f.isPaidLeave) return <Palmtree className="h-5 w-5 text-purple-400" />;
  if (f.isWageAdjustment && f.isDeduction) return <TrendingDown className="h-5 w-5 text-red-400" />;
  if (f.isWageAdjustment && f.isAddition) return <TrendingUp className="h-5 w-5 text-amber-400" />;
  if (f.isBaseWork) return <Briefcase className={`h-5 w-5 ${f.isBaseWorkUk ? 'text-slate-400' : 'text-cyan-400'}`} />;
  if (f.isVisaRun) return <FileCheck className="h-5 w-5 text-emerald-400" />;
  if (f.isWorkTrip) return <Ship className="h-5 w-5 text-blue-400" />;
  return <Plane className="h-5 w-5 text-purple-400" />;
};

export const getTripLocation = (trip, f) => {
  if (f.isHotelStay || f.isUkHotelStay) return trip.hotel_city;
  if (f.isHoliday) {
    if (trip.foreign_ports && trip.foreign_ports.length > 0) {
      return Array.isArray(trip.foreign_ports) ? trip.foreign_ports.join(', ') : trip.foreign_ports;
    }
    return null;
  }
  return null;
};
